import React from 'react';
import { 
  Shield, 
  Lock, 
  Eye, 
  Share2, 
  UserCheck, 
  Database, 
  AlertCircle, 
  Mail, 
  Phone, 
  FileText,
  Heart,
  Calendar,
  Bell,
  Key,
  Trash2,
  RefreshCw,
  ChevronRight,
  CheckCircle,
  Info,
  ArrowUp
} from 'lucide-react';
import 'bootstrap/dist/css/bootstrap.min.css';

const PrivacyPolicy = () => {
  const policyData = {
    title: "Privacy Policy",
    lastUpdated: "December 2023",
    version: "2.1",
    
    sections: [
      {
        id: "introduction",
        number: 1,
        title: "Introduction",
        icon: Shield,
        content: "Deda Hospital (\"we\", \"us\") is committed to protecting your privacy. This Privacy Policy explains how we collect, use, and safeguard your personal and health information when you use our mobile application.",
        bgColor: "bg-light"
      },
      {
        id: "information-collected",
        number: 2,
        title: "Information We Collect",
        icon: Database,
        content: "We may collect the following types of information:",
        items: [
          "Personal Information: Name, email, phone number, address.",
          "Health Information: Medical history, appointments, prescriptions.",
          "Usage Data: App interactions, device information, IP address."
        ],
        bgColor: "bg-white"
      },
      {
        id: "information-use",
        number: 3,
        title: "How We Use Your Information",
        icon: Lock,
        content: "Your information is used to:",
        items: [
          "Provide healthcare services (e.g., booking appointments).",
          "Improve app functionality.",
          "Communicate with you (e.g., notifications).",
          "Comply with legal obligations."
        ],
        bgColor: "bg-light"
      },
      {
        id: "information-sharing",
        number: 4,
        title: "Sharing Your Information",
        icon: Share2,
        content: "We may share your data with:",
        items: [
          "Healthcare providers for treatment.",
          "Third-party service providers (e.g., cloud storage), under strict confidentiality.",
          "Legal authorities, if required by law."
        ],
        warning: "We do not sell your information.",
        bgColor: "bg-white"
      },
      {
        id: "data-security",
        number: 5,
        title: "Data Security",
        icon: Key,
        content: "We use encryption, access controls, and other measures to protect your data. However, no system is completely secure, and we cannot guarantee absolute security.",
        bgColor: "bg-light"
      },
      {
        id: "your-rights",
        number: 6,
        title: "Your Rights",
        icon: UserCheck,
        content: "You may:",
        items: [
          "Access or update your personal information.",
          "Request deletion of your data, subject to legal retention requirements.",
          "Opt out of non-essential communications."
        ],
        note: "Contact us to exercise these rights.",
        bgColor: "bg-white"
      },
      {
        id: "data-retention",
        number: 7,
        title: "Data Retention",
        icon: Database,
        content: "We retain your data only as long as necessary for healthcare services or legal compliance, after which it is securely deleted.",
        bgColor: "bg-light"
      },
      {
        id: "policy-changes",
        number: 8,
        title: "Changes to This Policy",
        icon: AlertCircle,
        content: "We may update this Privacy Policy. Changes will be notified via the app. Continued use after updates indicates acceptance.",
        bgColor: "bg-white"
      },
      {
        id: "contact-us",
        number: 9,
        title: "Contact Us",
        icon: Mail,
        content: "For privacy-related questions, reach us at:",
        contacts: [
          { type: "Email", value: "info@dedahospital.com", link: "mailto:info@dedahospital.com" },
          { type: "Phone", value: "07000013332", link: "tel:07000013332" }
        ],
        bgColor: "bg-light"
      }
    ]
  };

  return (
    <div className="min-vh-100 bg-white">
      {/* Header */}
      <header className="border-bottom border-light py-4">
        <div className="container">
          <div className="row align-items-center">
            <div className="col-md-8">
              <div className="d-flex align-items-center">
                <div className="bg-success bg-opacity-10 p-3 rounded me-3">
                  <Shield className="text-success" size={28} />
                </div>
                <div>
                  <h1 className="h2 mb-0 text-dark">Privacy Policy</h1>
                  <p className="text-muted mb-0">
                    Last updated: {policyData.lastUpdated} • Version {policyData.version}
                  </p>
                </div>
              </div>
            </div>
            <div className="col-md-4 text-md-end mt-3 mt-md-0">
              <span className="badge bg-success bg-opacity-10 text-success px-3 py-2">
                Version {policyData.version}
              </span>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="py-5">
        <div className="container">
          {/* Introduction */}
          <div className="row justify-content-center">
            <div className="col-lg-10">
              <div className="mb-5">
                <p className="lead text-muted mb-4">
                  Welcome to Deda Hospital's Privacy Policy. This document outlines how we handle your personal 
                  and health information in compliance with healthcare regulations and best practices.
                </p>
                
                {/* Quick Info Cards */}
                <div className="row g-4 mb-5">
                  <div className="col-md-4">
                    <div className="card border-0 bg-success bg-opacity-5 h-100">
                      <div className="card-body text-center">
                        <Lock className="text-success mb-3" size={24} />
                        <h6 className="card-title">Secure Data</h6>
                        <p className="card-text text-muted small">Encrypted and protected</p>
                      </div>
                    </div>
                  </div>
                  <div className="col-md-4">
                    <div className="card border-0 bg-success bg-opacity-5 h-100">
                      <div className="card-body text-center">
                        <UserCheck className="text-success mb-3" size={24} />
                        <h6 className="card-title">Your Control</h6>
                        <p className="card-text text-muted small">Manage your data rights</p>
                      </div>
                    </div>
                  </div>
                  <div className="col-md-4">
                    <div className="card border-0 bg-success bg-opacity-5 h-100">
                      <div className="card-body text-center">
                        <Shield className="text-success mb-3" size={24} />
                        <h6 className="card-title">Transparent</h6>
                        <p className="card-text text-muted small">Clear and open policies</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Table of Contents */}
          <div className="row justify-content-center mb-5">
            <div className="col-lg-10">
              <div className="card border-0 shadow-sm">
                <div className="card-body p-4">
                  <h2 className="h4 mb-4">
                    <ChevronRight className="text-success me-2" size={20} />
                    Quick Navigation
                  </h2>
                  <div className="row g-3">
                    {policyData.sections.map((section) => (
                      <div key={section.id} className="col-md-6 col-lg-4">
                        <a 
                          href={`#${section.id}`}
                          className="d-flex align-items-center text-decoration-none text-dark p-3 border rounded hover-shadow"
                        >
                          <div className="bg-success bg-opacity-10 p-2 rounded me-3">
                            <span className="text-success fw-bold">{section.number}</span>
                          </div>
                          <span className="small">{section.title}</span>
                          <ChevronRight className="text-muted ms-auto" size={16} />
                        </a>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Policy Sections */}
          <div className="row justify-content-center">
            <div className="col-lg-10">
              {policyData.sections.map((section, index) => (
                <section 
                  key={section.id} 
                  id={section.id} 
                  className={`mb-5 ${section.bgColor} rounded-3 p-4 p-md-5`}
                >
                  {/* Section Header */}
                  <div className="d-flex align-items-start mb-4">
                    <div className="bg-success bg-opacity-10 p-3 rounded me-4">
                      <section.icon className="text-success" size={24} />
                    </div>
                    <div>
                      <div className="d-flex align-items-center mb-2">
                        <span className="badge bg-success bg-opacity-10 text-success px-3 py-1 me-2">
                          Section {section.number}
                        </span>
                      </div>
                      <h2 className="h3 mb-2">
                        <span className="text-success">{section.number}.</span> {section.title}
                      </h2>
                    </div>
                  </div>

                  {/* Section Content */}
                  <div className="ps-5 ps-md-6">
                    <p className="text-dark mb-4">{section.content}</p>

                    {/* Items List */}
                    {section.items && (
                      <div className="mb-4">
                        {section.items.map((item, idx) => (
                          <div key={idx} className="d-flex mb-2">
                            <CheckCircle className="text-success mt-1 me-3" size={18} />
                            <span className="text-dark">{item}</span>
                          </div>
                        ))}
                      </div>
                    )}

                    {/* Contacts */}
                    {section.contacts && (
                      <div className="mb-4">
                        <div className="row g-3">
                          {section.contacts.map((contact, idx) => (
                            <div key={idx} className="col-md-6">
                              <a 
                                href={contact.link}
                                className="d-flex align-items-center text-decoration-none p-3 border rounded bg-white hover-shadow"
                              >
                                <div className="bg-success bg-opacity-10 p-2 rounded me-3">
                                  {contact.type === 'Email' ? (
                                    <Mail className="text-success" size={18} />
                                  ) : (
                                    <Phone className="text-success" size={18} />
                                  )}
                                </div>
                                <div>
                                  <div className="text-muted small">{contact.type}</div>
                                  <div className="text-dark fw-medium">{contact.value}</div>
                                </div>
                              </a>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Warning */}
                    {section.warning && (
                      <div className="alert alert-warning border-0 mb-4">
                        <div className="d-flex">
                          <AlertCircle className="text-warning me-2 mt-1" size={20} />
                          <div className="text-dark">{section.warning}</div>
                        </div>
                      </div>
                    )}

                    {/* Note */}
                    {section.note && (
                      <div className="alert alert-info border-0 mb-4">
                        <div className="d-flex">
                          <Info className="text-info me-2 mt-1" size={20} />
                          <div className="text-dark">{section.note}</div>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Navigation */}
                  <div className="ps-5 ps-md-6 mt-4 pt-4 border-top border-light">
                    <a href="#top" className="text-decoration-none text-success small">
                      <ArrowUp className="me-1" size={16} />
                      Back to top
                    </a>
                  </div>
                </section>
              ))}
            </div>
          </div>

          {/* Call to Action */}
          <div className="row justify-content-center mt-5">
            <div className="col-lg-10">
              <div className="card border-0 bg-success text-white">
                <div className="card-body p-5">
                  <div className="row align-items-center">
                    <div className="col-md-8">
                      <h3 className="h2 mb-3">Need Help with Privacy?</h3>
                      <p className="mb-0 opacity-75">
                        Our privacy team is here to help you understand your rights and answer any questions.
                      </p>
                    </div>
                    <div className="col-md-4 text-md-end mt-4 mt-md-0">
                      <a 
                        href="mailto:info@dedahospital.com"
                        className="btn btn-light btn-lg px-4"
                      >
                        <Mail className="me-2" size={20} />
                        Contact Us
                      </a>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Additional Resources */}
          <div className="row justify-content-center mt-4">
            <div className="col-lg-10">
              <div className="d-flex flex-column flex-md-row justify-content-between align-items-center p-4 border rounded">
                <div className="d-flex align-items-center mb-3 mb-md-0">
                  <FileText className="text-success me-3" size={24} />
                  <div>
                    <h6 className="mb-0">Download Full Policy</h6>
                    <p className="text-muted small mb-0">PDF version for your records</p>
                  </div>
                </div>
                <div className="d-flex gap-3">
                  <button className="btn btn-outline-success">
                    Download PDF
                  </button>
                  <button className="btn btn-outline-secondary">
                    Print Version
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="bg-dark text-white py-5">
        <div className="container">
          <div className="row align-items-center">
            <div className="col-md-6 mb-4 mb-md-0">
              <div className="d-flex align-items-center">
                <Shield className="text-success me-3" size={24} />
                <div>
                  <h5 className="mb-1">Deda Hospital</h5>
                  <p className="text-light opacity-75 mb-0">Committed to your health and privacy</p>
                </div>
              </div>
            </div>
            <div className="col-md-6 text-md-end">
              <div className="mb-3">
                <p className="text-light opacity-75 mb-1">
                  © {new Date().getFullYear()} Deda Hospital. All rights reserved.
                </p>
                <p className="text-success mb-0">
                  <Phone className="me-2" size={16} />
                  Emergency: 07000013332
                </p>
              </div>
            </div>
          </div>
          <div className="text-center mt-4 pt-4 border-top border-secondary">
            <p className="text-light opacity-75 small mb-0">
              This privacy policy is compliant with healthcare regulations and data protection laws.
            </p>
          </div>
        </div>
      </footer>

      {/* Back to Top Button */}
      <a 
        href="#top" 
        className="position-fixed bottom-4 end-4 bg-success text-white p-3 rounded-circle shadow-lg text-decoration-none"
        style={{zIndex: 1000}}
      >
        <ArrowUp size={20} />
      </a>
    </div>
  );
};

export default PrivacyPolicy;